import { appAlert } from '@/lib/appAlert';
﻿import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, Text, TextInput, useWindowDimensions, View, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { NeonNotifIcon } from '@/components/ui/NeonNotifIcon';
import { fetchMyNotifications, fetchNotificationEventsPage, markAllNotificationsRead, markNotificationRead, type AppNotification } from '@/lib/notifications';
import { listMyChatPreviews, markAgreementMessagesRead, type ChatPreview } from '@/lib/agreementMessages';
import { formatDateTime } from '@/lib/format';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { LevelBadge, levelColor, type LevelInfo } from '@/components/ui/LevelBadge';
import { formatUserId } from '@/lib/format';
import { DisclaimerNote } from '@/components/DisclaimerNote';
import { Coachmarks, type CoachTarget, type Rect } from '@/components/Coachmarks';
import { DailyCheckin } from '@/components/DailyCheckin';
import { useIsWideWeb } from '@/components/WebShell';
import { setTutorialDismissListener } from '@/components/Tutorial';
import { CHAT_AQUA, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { TabScreenWrapper } from '@/components/TabScreenWrapper';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useOnAppActive } from '@/hooks/useOnAppActive';
import type { ReviewSummary } from '@/lib/database.types';
import { countActiveAgreements, getAgreementStats, myFreeRemaining } from '@/lib/agreements';
import { getMyJuryDuties, type JuryDuty } from '@/lib/disputes';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { reliabilityCap, safeNum } from '@/lib/format';
import { claimPendingInviteAgreement, consumePendingInviteAgreement } from '@/lib/invite';
import { getReviewSummary } from '@/lib/reviews';
import { getUserLevel } from '@/lib/levels';
import { effectiveAvatarUrl } from '@/lib/avatar';
import { listMyConnections } from '@/lib/connections';
import { getCircleAnnouncements } from '@/lib/announcements';
import { countNewOpenRequests, countOffersOnMyRequests, fetchOpenRequests } from '@/lib/serviceRequests';
import { getMySponsoredListing, getProfessionalCountsByRegion, type MySponsoredListing } from '@/lib/professionals';
import { fetchMySecondaryCategories } from '@/lib/secondaryCategories';
import { supabase } from '@/lib/supabase';
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';
const NAVY = '#0D1B2A';
const GOLD = '#C9A843';

type NotifKind = 'action' | 'success' | 'danger' | 'info';

const NOTIF_KIND_BY_TYPE: Record<string, NotifKind> = {
  // Χρειάζονται ενέργεια από τον χρήστη
  agreement_pending: 'action',
  agreement_sent: 'action',
  completion_requested: 'action',
  payment_declared: 'action',
  cancel_requested: 'action',
  edit_requested: 'action',
  edit_awaiting_final: 'action',
  dispute_open: 'action',
  dispute_vote_needed: 'action',
  jury_selected: 'action',
  offer_pending: 'action',
  new_offer: 'action',
  advance_payment_proposed: 'action',
  connection_request: 'action',
  identity_verification_pending: 'action',
  // Θετική έκβαση
  agreement_signed: 'success',
  party_signed: 'success',
  agreement_completed: 'success',
  payment_confirmed: 'success',
  cancel_accepted: 'success',
  edit_request_approved: 'success',
  edit_final_approved: 'success',
  offer_accepted: 'success',
  connection_accepted: 'success',
  review_received: 'success',
  level_up: 'success',
  advance_payment_responded: 'success',
  // Αρνητική έκβαση / ακύρωση
  agreement_declined: 'danger',
  agreement_auto_cancelled: 'danger',
  agreement_withdrawn: 'danger',
  agreement_disputed: 'danger',
  agreement_terminated: 'danger',
  agreement_overdue: 'danger',
  cancel_refused: 'danger',
  completion_rejected: 'danger',
  edit_request_rejected: 'danger',
  edit_final_rejected: 'danger',
  dispute_admin_review: 'danger',
  offer_expired: 'danger',
  // Ενημερωτικό
  dispute_resolved: 'info',
  dispute_withdrawn: 'info',
  announcement: 'info',
  identity_verification_result: 'info',
  renewal_prompt: 'info',
};

const NOTIF_ICON_BY_TYPE: Record<string, keyof typeof Ionicons.glyphMap> = {
  agreement_pending: 'create-outline',
  agreement_sent: 'document-text-outline',
  agreement_signed: 'checkmark-circle-outline',
  party_signed: 'checkmark-circle-outline',
  agreement_completed: 'trophy-outline',
  agreement_declined: 'close-circle-outline',
  agreement_auto_cancelled: 'close-circle-outline',
  agreement_withdrawn: 'arrow-undo-outline',
  agreement_overdue: 'time-outline',
  agreement_disputed: 'warning-outline',
  agreement_terminated: 'stop-circle-outline',
  completion_requested: 'flag-outline',
  completion_rejected: 'flag-outline',
  payment_declared: 'cash-outline',
  payment_confirmed: 'cash-outline',
  advance_payment_proposed: 'cash-outline',
  advance_payment_responded: 'cash-outline',
  cancel_requested: 'ban-outline',
  cancel_accepted: 'ban-outline',
  cancel_refused: 'ban-outline',
  edit_requested: 'create-outline',
  edit_request_approved: 'create-outline',
  edit_request_rejected: 'create-outline',
  edit_awaiting_final: 'create-outline',
  edit_final_rejected: 'create-outline',
  edit_final_approved: 'create-outline',
  dispute_open: 'alert-circle-outline',
  dispute_vote_needed: 'alert-circle-outline',
  jury_selected: 'people-outline',
  dispute_admin_review: 'alert-circle-outline',
  dispute_resolved: 'alert-circle-outline',
  dispute_withdrawn: 'alert-circle-outline',
  new_offer: 'briefcase-outline',
  offer_pending: 'briefcase-outline',
  offer_accepted: 'briefcase-outline',
  offer_expired: 'briefcase-outline',
  connection_request: 'people-outline',
  connection_accepted: 'people-outline',
  review_received: 'star-outline',
  level_up: 'ribbon-outline',
  announcement: 'megaphone-outline',
  identity_verification_pending: 'shield-checkmark-outline',
  identity_verification_result: 'shield-checkmark-outline',
  renewal_prompt: 'refresh-outline',
};

function notifKindColor(kind: NotifKind, colors: AppColors): string {
  if (kind === 'success') return colors.success;
  if (kind === 'danger') return colors.danger;
  if (kind === 'action') return GOLD;
  return colors.textMuted;
}

function notifVisual(type: string, colors: AppColors): { icon: keyof typeof Ionicons.glyphMap; color: string } {
  const kind = NOTIF_KIND_BY_TYPE[type] ?? 'info';
  return {
    icon: NOTIF_ICON_BY_TYPE[type] ?? 'notifications-outline',
    color: notifKindColor(kind, colors),
  };
}

function LevelProgressBar({ levelInfo }: { levelInfo: LevelInfo }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const progressAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const ratio = levelInfo.required_next > 0
      ? Math.min(levelInfo.completed / levelInfo.required_next, 1)
      : 1;
    Animated.timing(progressAnim, {
      toValue: ratio,
      duration: 700,
      useNativeDriver: false,
    }).start();
  }, [levelInfo.completed, levelInfo.required_next, progressAnim]);

  const color = levelColor(levelInfo.level);

  if (levelInfo.level >= 100) {
    return (
      <View style={styles.progressWrap}>
        <Text style={[styles.progressText, { color: GOLD }]}>{t('home.level_max')}</Text>
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: '100%', backgroundColor: color }]} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.progressWrap}>
      <Text style={styles.progressText}>
        <Text style={{ color: colors.textMuted }}>
          {t('home.level_progress', { count: levelInfo.required_next - levelInfo.completed })}
        </Text>
      </Text>
      <View style={styles.progressTrack}>
        <Animated.View
          style={[
            styles.progressFill,
            {
              backgroundColor: color,
              width: progressAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%'],
              }),
            },
          ]}
        />
      </View>
    </View>
  );
}

export default function HomeScreen() {
  const { user } = useAuth();
  const { profile, refresh: refreshProfile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const isWideWeb = useIsWideWeb();
  const { colors } = useTheme();
  const { height: screenHeight } = useWindowDimensions();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();

  const displayName = profile?.full_name?.trim().split(' ')[0] || user?.email;

  const { count: needsAction, breakdown, refresh: refreshNeedsAction } = useNeedsAction();
  const [activeOneOff, setActiveOneOff] = useState(0);
  const [activeOngoing, setActiveOngoing] = useState(0);
  const [connectionCount, setConnectionCount] = useState(0);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [freeRemaining, setFreeRemaining] = useState<number | null>(null);
  const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [hasUnreadAnnouncements, setHasUnreadAnnouncements] = useState(false);
  const [hasNewNews, setHasNewNews] = useState(false);
  const [newsTitles, setNewsTitles] = useState<string[]>([]);
  const [newsTitleIndex, setNewsTitleIndex] = useState(0);
  const newsTitleFade = useRef(new Animated.Value(1)).current;
  const [newRequestsCount, setNewRequestsCount] = useState(0);
  const [newOffersOnMyRequestsCount, setNewOffersOnMyRequestsCount] = useState(0);
  // Ανοιχτά αιτήματα στον ίδιο νομό ΚΑΙ ίδια κατηγορία επαγγέλματος με τον
  // επαγγελματία — highlight πάνω στο "Βρες υποψήφιους πελάτες" tile.
  const [nearbyMatchingRequestsCount, setNearbyMatchingRequestsCount] = useState(0);
  const nearbyBounceAnim = useRef(new Animated.Value(0)).current;
  const nearbyGlowAnim = useRef(new Animated.Value(0)).current;
  // Δημόσιοι επαγγελματίες στον ίδιο νομό με τον χρήστη — highlight πάνω στο
  // "Βρες επαγγελματίες" tile.
  const [nearbyProfessionalsCount, setNearbyProfessionalsCount] = useState(0);
  const [showBellPanel, setShowBellPanel] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [loadingMoreEvents, setLoadingMoreEvents] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [chatPreviews, setChatPreviews] = useState<ChatPreview[]>([]);
  const [chatPreviewsLoading, setChatPreviewsLoading] = useState(false);
  const [platformTotal, setPlatformTotal] = useState<number | null>(null);
  const [platformOffersSent, setPlatformOffersSent] = useState<number | null>(null);
  const [platformUsers, setPlatformUsers] = useState<number | null>(null);
  const [platformDisputesResolved, setPlatformDisputesResolved] = useState<number | null>(null);
  const [platformProfessionals, setPlatformProfessionals] = useState<number | null>(null);
  const [platformEurTotal, setPlatformEurTotal] = useState<number | null>(null);
  const [platformStatIndex, setPlatformStatIndex] = useState(0);
  const [pendingJuryDuty, setPendingJuryDuty] = useState<JuryDuty | null>(null);
  const [juryDutyNowTick, setJuryDutyNowTick] = useState(() => Date.now());
  const platformStatFade = useRef(new Animated.Value(1)).current;
  const [tickerIndex, setTickerIndex] = useState(0);
  const tickerFade = useRef(new Animated.Value(1)).current;

  const COACH_KEY = 'coachmarks_v2';
  const profileRef = useRef<View>(null);
  const circleRef = useRef<View>(null);
  const agreementsRef = useRef<View>(null);
  const newRef = useRef<View>(null);
  const coachStarted = useRef(false);
  const rootRef = useRef<View>(null);
  const [coachTargets, setCoachTargets] = useState<CoachTarget[] | null>(null);

  const measureRef = useCallback((ref: React.RefObject<View | null>): Promise<Rect | null> => {
    return new Promise((resolve) => {
      const node = ref.current;
      const root = rootRef.current;
      if (!node || !root || !node.measureLayout) return resolve(null);
      node.measureLayout(
        root as never,
        (x, y, w, h) => resolve(w > 0 && h > 0 ? { x, y, width: w, height: h } : null),
        () => resolve(null),
      );
    });
  }, []);

  const maybeStartCoachmarks = useCallback(async () => {
    if (coachStarted.current) return;
    try {
      if (await AsyncStorage.getItem(COACH_KEY)) return;
    } catch {
      return;
    }
    const targets: CoachTarget[] = [
      { ref: profileRef, title: t('home.coach_profile_title'), text: t('home.coach_profile_text') },
      { ref: circleRef, title: t('home.coach_circle_title'), text: t('home.coach_circle_text') },
      { ref: agreementsRef, title: t('home.coach_agreements_title'), text: t('home.coach_agreements_text') },
      { ref: newRef, title: t('home.coach_new_title'), text: t('home.coach_new_text') },
    ];
    coachStarted.current = true;
    setCoachTargets(targets);
  }, [t]);

  // Αν το "πρώτη προσφορά" alert έπεσε ενώ το coachmarks tour επρόκειτο να
  // ξεκινήσει, δεν το δείχνουμε αμέσως (θα στοιβαζόταν πάνω στο tour) — το
  // κρατάμε εδώ και το δείχνουμε μόλις τελειώσει το tour.
  const pendingFirstOfferAlertRef = useRef(false);

  function showFirstOfferAlert() {
    appAlert(
      t('home.firstOfferAlertTitle'),
      t('home.firstOfferAlertBody'),
      [
        { text: t('home.later'), style: 'cancel' },
        { text: t('home.createOffer'), onPress: () => router.push('/(app)/offers/new') },
      ],
    );
  }

  const finishCoach = useCallback(async () => {
    try { await AsyncStorage.setItem(COACH_KEY, '1'); } catch {}
    setCoachTargets(null);
    // Νέος χρήστης: τον στέλνουμε να δημιουργήσει την υπογραφή του
    setTimeout(() => router.push({ pathname: '/(app)/profile', params: { section: 'signature' } }), 300);
    if (pendingFirstOfferAlertRef.current) {
      pendingFirstOfferAlertRef.current = false;
      setTimeout(() => showFirstOfferAlert(), 900);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    setTutorialDismissListener(() => {
      setTimeout(() => void maybeStartCoachmarks(), 450);
    });
    return () => setTutorialDismissListener(null);
  }, [maybeStartCoachmarks]);

  const refreshHomeData = useCallback(() => {
    if (!user) return;
    void refreshProfile();
    void refreshNeedsAction();
    void countActiveAgreements(user.id)
      .then((c) => { setActiveOneOff(c.oneOff); setActiveOngoing(c.ongoing); })
      .catch(() => { setActiveOneOff(0); setActiveOngoing(0); });
    void getReviewSummary(user.id).then(setSummary).catch(() => setSummary(null));
    void myFreeRemaining().then(setFreeRemaining).catch(() => setFreeRemaining(null));
    void getUserLevel().then(setLevelInfo).catch(() => setLevelInfo(null));
    void listMyConnections().then((c) => setConnectionCount(c.length)).catch(() => {});
    void (async () => {
      try {
        const anns = await getCircleAnnouncements();
        if (anns.length === 0) { setHasUnreadAnnouncements(false); return; }
        const lastSeen = await AsyncStorage.getItem('announcements_last_seen');
        const latest = anns.reduce((max, a) => (a.created_at > max ? a.created_at : max), anns[0].created_at);
        setHasUnreadAnnouncements(!lastSeen || latest > lastSeen);
      } catch { setHasUnreadAnnouncements(false); }
    })();
    void (async () => {
      try {
        const lastSeen = await AsyncStorage.getItem('news_last_seen');
        const { data } = await supabase
          .from('news_items')
          .select('title, published_at')
          .order('published_at', { ascending: false })
          .limit(5);
        const rows = (data ?? []) as { title: string; published_at: string }[];
        if (rows[0]?.published_at) {
          setHasNewNews(!lastSeen || rows[0].published_at > lastSeen);
        }
        setNewsTitles(rows.map((r) => r.title).filter(Boolean));
      } catch { setHasNewNews(false); }
    })();
    if (profile?.portfolio_enabled) {
      void (async () => {
        try {
          const lastSeen = await AsyncStorage.getItem('requests_last_seen');
          setNewRequestsCount(await countNewOpenRequests(lastSeen, user?.id));
        } catch { setNewRequestsCount(0); }
      })();
    } else {
      setNewRequestsCount(0);
    }
    if (profile?.portfolio_enabled && profile.profession_category_id && profile.region && user?.id) {
      void fetchMySecondaryCategories(user.id)
        .then((secondary) => {
          const categoryIds = [profile.profession_category_id!, ...secondary.map((c) => c.id)];
          return fetchOpenRequests(categoryIds, user.id, profile.region);
        })
        .then((list) => setNearbyMatchingRequestsCount(list.length))
        .catch(() => setNearbyMatchingRequestsCount(0));
    } else {
      setNearbyMatchingRequestsCount(0);
    }
    if (profile?.region) {
      void getProfessionalCountsByRegion(null, null)
        .then((counts) => setNearbyProfessionalsCount(counts[profile.region!] ?? 0))
        .catch(() => setNearbyProfessionalsCount(0));
    } else {
      setNearbyProfessionalsCount(0);
    }
    void (async () => {
      try {
        const lastSeen = await AsyncStorage.getItem('my_requests_offers_last_seen');
        setNewOffersOnMyRequestsCount(await countOffersOnMyRequests(lastSeen));
      } catch { setNewOffersOnMyRequestsCount(0); }
    })();
    void getAgreementStats().then((s) => {
      setPlatformTotal(s.total);
      setPlatformOffersSent(s.offers_sent);
      setPlatformUsers(s.users);
      setPlatformDisputesResolved(s.disputes_resolved);
      setPlatformProfessionals(s.professionals);
      setPlatformEurTotal(s.eur_total);
    }).catch(() => {});
    void getMyJuryDuties()
      .then((duties) => {
        const pending = duties.filter((d) => !d.already_voted);
        const earliest = pending.sort((a, b) => new Date(a.my_vote_deadline).getTime() - new Date(b.my_vote_deadline).getTime())[0];
        setPendingJuryDuty(earliest ?? null);
      })
      .catch(() => setPendingJuryDuty(null));
    void AsyncStorage.getItem('tutorial_v1').then((t) => {
      if (t) setTimeout(() => void maybeStartCoachmarks(), 600);
    });
  }, [user, maybeStartCoachmarks, refreshProfile, refreshNeedsAction, profile?.portfolio_enabled]);

  useFocusEffect(useCallback(() => { refreshHomeData(); }, [refreshHomeData]));
  useOnAppActive(refreshHomeData);

  // Glow + bounce στην ένδειξη "αιτήματα κοντά σου" όσο υπάρχει τουλάχιστον ένα.
  useEffect(() => {
    if (nearbyMatchingRequestsCount <= 0) return;
    const bounce = Animated.loop(
      Animated.sequence([
        Animated.timing(nearbyBounceAnim, { toValue: 1, duration: 420, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(nearbyBounceAnim, { toValue: 0, duration: 420, useNativeDriver: Platform.OS !== 'web' }),
        Animated.delay(900),
      ]),
    );
    const glow = Animated.loop(
      Animated.sequence([
        Animated.timing(nearbyGlowAnim, { toValue: 1, duration: 700, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(nearbyGlowAnim, { toValue: 0, duration: 700, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    bounce.start();
    glow.start();
    return () => { bounce.stop(); glow.stop(); };
  }, [nearbyMatchingRequestsCount, nearbyBounceAnim, nearbyGlowAnim]);

  // Η ένδειξη "στη περιοχή σου" επαγγελματιών παραμένει σταθερή, χωρίς animation.

  useEffect(() => {
    void consumePendingInviteAgreement().then(async (pending) => {
      if (!pending) return;
      // Ο κωδικός πρόσκλησης (αν υπάρχει) τον δηλώνει αντισυμβαλλόμενο πριν
      // ανοίξει η συμφωνία — αλλιώς το RLS θα την έκρυβε.
      await claimPendingInviteAgreement(pending);
      router.push({ pathname: '/(app)/agreements/[id]', params: { id: pending } });
    });
    void AsyncStorage.getItem('pending_offer_id').then(async (offerId) => {
      if (!offerId) return;
      await AsyncStorage.removeItem('pending_offer_id');
      // Δεν κάνουμε αυτόματη αποδοχή — ο χρήστης μόλις εγγράφηκε, πρέπει να δει
      // την προσφορά και να αποφασίσει ο ίδιος (Αποδοχή/Απόρριψη).
      router.push({ pathname: '/(app)/offers/[id]', params: { id: offerId } });
    });
    void AsyncStorage.getItem('prompt_first_offer').then(async (flag) => {
      if (!flag) return;
      await AsyncStorage.removeItem('prompt_first_offer');
      // Αν το coachmarks tour δεν έχει τρέξει ακόμα γι' αυτόν τον χρήστη, μη
      // δείξεις το alert τώρα (θα στοιβαζόταν πάνω στο tour) — δείξ' το μόλις
      // τελειώσει (βλ. finishCoach).
      const coachSeen = await AsyncStorage.getItem(COACH_KEY);
      if (!coachSeen && !coachStarted.current) {
        pendingFirstOfferAlertRef.current = true;
        return;
      }
      showFirstOfferAlert();
    });
  }, [router]);

  const [showPrivateSponsorPrompt, setShowPrivateSponsorPrompt] = useState(false);
  const [makingPublicBusy, setMakingPublicBusy] = useState(false);
  const [mySponsoredListing, setMySponsoredListing] = useState<MySponsoredListing | null>(null);

  useEffect(() => {
    if (!profile?.portfolio_enabled) { setMySponsoredListing(null); return; }
    void getMySponsoredListing().then(setMySponsoredListing).catch(() => setMySponsoredListing(null));
  }, [profile?.portfolio_enabled]);

  function onPromoteProfilePress() {
    if (profile?.portfolio_public) {
      router.push({ pathname: '/(app)/professional-account', params: { openSponsor: '1' } } as any);
    } else {
      setShowPrivateSponsorPrompt(true);
    }
  }

  async function onMakePublicAndPromote() {
    if (!user) return;
    setMakingPublicBusy(true);
    try {
      await supabase.from('profiles').update({ portfolio_public: true }).eq('id', user.id);
      await refreshProfile();
      setShowPrivateSponsorPrompt(false);
      router.push({ pathname: '/(app)/professional-account', params: { openSponsor: '1' } } as any);
    } catch {
      // αγνόησε — ο χρήστης μπορεί να ξαναδοκιμάσει από το προφίλ του
    } finally {
      setMakingPublicBusy(false);
    }
  }
  type TickerSlide = {
    key: string;
    icon: keyof typeof Ionicons.glyphMap;
    iconColor: string;
    text: string;
    boldText?: string;
    onPress: () => void;
  };

  const walletBalance = Number(profile?.wallet_balance ?? 0);
  const freeCount = freeRemaining !== null && freeRemaining > 0 ? freeRemaining : 0;
  // Το "max" της μπάρας μελανιού είναι το υπόλοιπο αμέσως μετά την τελευταία
  // ΠΡΑΓΜΑΤΙΚΗ αγορά (όχι refund/reward) — βλ. complete_wallet_topup. Δεν
  // υπάρχει ακόμα στο generated Profile type.
  const inkMaxReference = Number((profile as (typeof profile & { ink_max_reference?: number }) | null)?.ink_max_reference ?? 0);
  const inkFillRatio = inkMaxReference > 0 ? Math.max(0, Math.min(1, walletBalance / inkMaxReference)) : null;

  // Σύντομες εναλλασσόμενες φράσεις μελανιού/δωρεάν υπογραφών, ενσωματωμένες
  // μέσα στο πλακίδιο "Νέα Συμφωνία" (όχι πια ξεχωριστό banner).
  const tickerSlides = useMemo<TickerSlide[]>(() => {
    const slides: TickerSlide[] = [];
    if (freeCount > 0) {
      slides.push({
        key: 'free',
        icon: 'gift-outline',
        iconColor: GOLD,
        text: t('home.newAgreementHint_free', { count: freeCount }),
        onPress: () => setShowFreeModal(true),
      });
    }
    return slides;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletBalance, freeCount, colors, t]);

  useEffect(() => {
    if (tickerIndex >= tickerSlides.length) setTickerIndex(0);
  }, [tickerSlides.length, tickerIndex]);

  useEffect(() => {
    if (tickerSlides.length <= 1) return;
    const timer = setInterval(() => {
      Animated.timing(tickerFade, { toValue: 0, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start(() => {
        setTickerIndex((i) => (i + 1) % tickerSlides.length);
        Animated.timing(tickerFade, { toValue: 1, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start();
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [tickerSlides.length, tickerFade]);

  useEffect(() => {
    if (!pendingJuryDuty) return;
    const timer = setInterval(() => setJuryDutyNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [pendingJuryDuty]);

  const juryDutyCountdownText = useMemo(() => {
    if (!pendingJuryDuty) return '';
    const msLeft = new Date(pendingJuryDuty.my_vote_deadline).getTime() - juryDutyNowTick;
    if (msLeft <= 0) return t('home.juryDutyExpiringNow');
    const totalMinutes = Math.floor(msLeft / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0
      ? t('home.juryDutyCountdownHours', { hours, minutes })
      : t('home.juryDutyCountdownMinutes', { minutes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingJuryDuty, juryDutyNowTick, t]);

  const platformStats = useMemo<string[]>(() => {
    const stats: string[] = [];
    if (platformTotal != null && platformTotal > 0) {
      stats.push(t('home.platformStatFull', { count: platformTotal.toLocaleString('el-GR') }));
    }
    if (platformOffersSent != null && platformOffersSent > 0) {
      stats.push(t('home.platformStatOffers', { count: platformOffersSent.toLocaleString('el-GR') }));
    }
    if (platformUsers != null && platformUsers > 0) {
      stats.push(t('home.platformStatUsers', { count: platformUsers.toLocaleString('el-GR') }));
    }
    if (platformDisputesResolved != null && platformDisputesResolved > 0) {
      stats.push(t('home.platformStatDisputes', { count: platformDisputesResolved.toLocaleString('el-GR') }));
    }
    if (platformProfessionals != null && platformProfessionals > 0) {
      stats.push(t('home.platformStatProfessionals', { count: platformProfessionals.toLocaleString('el-GR') }));
    }
    if (platformEurTotal != null && platformEurTotal > 0) {
      stats.push(t('home.platformStatEur', { count: Math.round(platformEurTotal).toLocaleString('el-GR') }));
    }
    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformTotal, platformOffersSent, platformUsers, platformDisputesResolved, platformProfessionals, platformEurTotal, t]);

  useEffect(() => {
    if (platformStatIndex >= platformStats.length) setPlatformStatIndex(0);
  }, [platformStats.length, platformStatIndex]);

  useEffect(() => {
    if (platformStats.length <= 1) return;
    const timer = setInterval(() => {
      Animated.timing(platformStatFade, { toValue: 0, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start(() => {
        setPlatformStatIndex((i) => (i + 1) % platformStats.length);
        Animated.timing(platformStatFade, { toValue: 1, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start();
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [platformStats.length, platformStatFade]);

  // Εναλλαγή τίτλων ειδήσεων στο πλακίδιο "Εφημερίδα".
  useEffect(() => {
    if (newsTitles.length <= 1) return;
    const timer = setInterval(() => {
      Animated.timing(newsTitleFade, { toValue: 0, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start(() => {
        setNewsTitleIndex((i) => (i + 1) % newsTitles.length);
        Animated.timing(newsTitleFade, { toValue: 1, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start();
      });
    }, 3500);
    return () => clearInterval(timer);
  }, [newsTitles.length, newsTitleFade]);

  const hasReviews = !!summary && summary.review_count > 0;
  const rating = safeNum(summary?.effective_rating);
  // Το badge του κουδουνιού πρέπει να αντανακλά ΟΛΑ όσα δείχνει το πάνελ όταν
  // ανοίγει (fetchMyNotifications: συμφωνίες + προσφορές + συνδέσεις + unread
  // events) — πριν έδειχνε μόνο unreadEvents, οπότε φαινόταν πάντα "1" ακόμα
  // κι όταν το πάνελ είχε πολλαπλά στοιχεία εκκρεμών ενεργειών.
  const bellUnreadCount = breakdown.agreements + breakdown.offers + breakdown.connections + breakdown.unreadEvents;

  // ── Εναλλασσόμενα στατιστικά κάτω από το όνομα (rating / αξιοπιστία / επίπεδο) ──
  const completionText = summary?.completion_rate == null
    ? `✅ ${levelInfo?.completed ?? 0}`
    : `✅ ${Math.min(summary.completion_rate, reliabilityCap(summary, profile?.phone_verified, profile?.verified))}%`;

  // Το επίπεδο πλέον κάθεται σταθερά κάτω από την εικόνα προφίλ (LevelBadge),
  // οπότε εδώ μένουν μόνο τα δύο στατιστικά — δίπλα-δίπλα, χωρίς εναλλαγή.

  return (
    <TabScreenWrapper>
    <View ref={rootRef} style={styles.root}>
      <ScrollView
        style={styles.flex1}
        contentContainerStyle={[styles.container, styles.containerGrow, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl }]}
        showsVerticalScrollIndicator={false}
      >

        {/* ── Header + Level bar ── */}
        <View style={styles.headerGroup}>
        <View style={styles.header}>
          <View style={styles.avatarCol}>
            <Pressable
              ref={profileRef}
              style={styles.avatarWrap}
              onPress={() => router.push('/(app)/profile')}
              accessibilityRole="button"
              accessibilityLabel={t('home.myProfile')}
            >
              <Avatar uri={effectiveAvatarUrl(profile)} name={profile?.full_name} size={52} showCameraPrompt={!effectiveAvatarUrl(profile)} />
              {profile?.verified && (
                <View style={styles.verifiedOverlay}>
                  <VerifiedBadge compact />
                </View>
              )}
            </Pressable>
            {!!levelInfo && (
              <View style={styles.levelUnderAvatar}>
                <LevelBadge info={levelInfo} size="md" />
              </View>
            )}
          </View>

          <View style={styles.headerMid}>
            <Text style={styles.greeting} numberOfLines={1}>
              <Text style={styles.greetingName}>{displayName}</Text>
            </Text>
            {!!profile?.user_number && (
              <View style={styles.userIdRow}>
                <Text style={styles.userIdText}>{formatUserId(profile.user_number)}</Text>
                <Pressable
                  hitSlop={8}
                  onPress={() => {
                    const profileUrl = `${SUPABASE_URL}/functions/v1/profile-page/${user?.id}`;
                    const ratingText = summary?.completion_rate != null
                      ? ` · ✅ ${Math.min(summary.completion_rate, reliabilityCap(summary, profile?.phone_verified, profile?.verified))}%`
                      : '';
                    void Share.share({
                      title: t('home.shareProfileTitle', { name: profile.full_name?.trim() || t('home.defaultUserName') }),
                      message: `${t('home.shareProfileMessage', { rating: ratingText })}\n${profileUrl}`,
                      url: profileUrl,
                    });
                  }}
                >
                  <Ionicons name="share-outline" size={12} color={colors.textMuted} />
                </Pressable>
              </View>
            )}
            {/* Βαθμολογία + αξιοπιστία, δίπλα-δίπλα, σταθερά (όχι εναλλαγή) —
                πιο άχρωμα ώστε να μη βαραίνουν στο μάτι. Πατώντας το καθένα
                εξηγεί τι σημαίνει. */}
            <View style={styles.statsRow}>
              <Pressable
                onPress={() => appAlert(t('home.statsExplainTitle'), t('home.statsExplainRating'))}
                accessibilityRole="button"
                hitSlop={6}
              >
                <Text style={styles.statChip} numberOfLines={1}>
                  {hasReviews ? `⭐ ${rating.toFixed(1)}` : '⭐ —'}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => appAlert(t('home.statsExplainTitle'), t('home.statsExplainReliability'))}
                accessibilityRole="button"
                hitSlop={6}
              >
                <Text style={styles.statChip} numberOfLines={1}>{completionText}</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.headerActions}>
            {/* Chat bubble — μόνιμα ορατό, γκρι όταν δεν υπάρχει κάτι νέο, χρυσό+glow όταν υπάρχει.
                Πατώντας ανοίγει ιστορικό με το τελευταίο μήνυμα ανά συμφωνία (ίδια λογική με το κουδούνι). */}
            <Pressable
              style={styles.chatGlowBtn}
              onPress={async () => {
                setShowChatPanel(true);
                setChatPreviewsLoading(true);
                try {
                  setChatPreviews(await listMyChatPreviews());
                } catch {
                  setChatPreviews([]);
                } finally {
                  setChatPreviewsLoading(false);
                }
              }}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel={breakdown.unreadChat > 0 ? t('home.unreadChat') : t('home.chat')}
            >
              <NeonNotifIcon
                size={24}
                color={CHAT_AQUA}
                iconName="chatbubble-ellipses"
                iconNameInactive="chatbubble-ellipses-outline"
                active={breakdown.unreadChat > 0}
                count={breakdown.unreadChat}
              />
            </Pressable>
            {/* Κουδούνι — πάντα ορατό, ringing μόνο αν υπάρχουν unread */}
            <Pressable
              style={styles.bellBtn}
              onPress={async () => {
                setShowBellPanel(true);
                setNotifLoading(true);
                try {
                  const [actionable, eventsPage] = await Promise.all([
                    fetchMyNotifications(user!.id),
                    fetchNotificationEventsPage(user!.id, {}),
                  ]);
                  setHasMoreEvents(eventsPage.hasMore);
                  const merged = [...actionable, ...eventsPage.events]
                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                  setNotifications(merged);
                } catch {
                  setNotifications([]);
                  setHasMoreEvents(false);
                } finally {
                  setNotifLoading(false);
                }
              }}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel={t('home.notifications')}
            >
              <NeonNotifIcon
                size={26}
                color={GOLD}
                iconName="notifications"
                iconNameInactive="notifications-outline"
                active={bellUnreadCount > 0}
                count={bellUnreadCount}
              />
            </Pressable>
            {/* Κύκλος μου + επαφές στη Χειραψία (ενοποιημένα οπτικά, ίδιο "δίκτυο") */}
            <View style={styles.circleCol}>
              <Pressable
                ref={circleRef}
                collapsable={false}
                style={styles.headerBtn}
                onPress={() => router.push('/(app)/circle')}
                hitSlop={10}
                accessibilityRole="button"
                accessibilityLabel={t('home.myCircle')}
              >
                <View style={styles.circleIconOuter}>
                  <View style={[styles.circleIconWrap, breakdown.circleSize > 0 && styles.circleIconWrapFilled]}>
                    {breakdown.circleSize > 0 ? (
                      <View style={styles.circleIconInner}>
                        <Text
                          style={styles.circleIconCount}
                          adjustsFontSizeToFit
                          minimumFontScale={0.4}
                          numberOfLines={1}
                        >
                          {breakdown.circleSize}
                        </Text>
                        <Text style={styles.circleIconSub}>{t('home.circlePeople')}</Text>
                      </View>
                    ) : (
                      <Ionicons name="people-outline" size={22} color={colors.textMuted} />
                    )}
                  </View>
                  {/* Κόκκινο badge μόνο αν υπάρχουν εκκρεμή αιτήματα */}
                  {breakdown.connections > 0 && (
                    <View style={styles.circleBadgeRed}>
                      <Text style={styles.circleBadgeText}>
                        {breakdown.connections > 9 ? '9+' : breakdown.connections}
                      </Text>
                    </View>
                  )}
                  {hasUnreadAnnouncements && (
                    <View style={styles.announcementDot}>
                      <Ionicons name="megaphone" size={9} color="#fff" />
                    </View>
                  )}
                </View>
                <Text style={styles.circleLabel} numberOfLines={1}>{t('home.myCircle')}</Text>
              </Pressable>
            </View>

          </View>
        </View>

        {/* ── Level progress bar ── */}
        {levelInfo && (
          <LevelProgressBar levelInfo={levelInfo} />
        )}
        </View>

        {pendingJuryDuty && (
          <Pressable
            style={styles.juryDutyTile}
            onPress={() => router.push('/(app)/jury' as any)}
            accessibilityRole="button"
          >
            <Text style={styles.juryDutyText}>{t('home.juryDutySelected')}</Text>
            <Text style={styles.juryDutyCountdown}>{juryDutyCountdownText}</Text>
          </Pressable>
        )}

        {/* Σε desktop web οι ενέργειες μπαίνουν σε δύο στήλες — σε κινητό ο
            wrapper είναι απλό View και δεν αλλάζει τίποτα. */}
        <View style={isWideWeb ? styles.wideGrid : styles.stackGap}>

        <Pressable
          style={({ pressed }) => [styles.heroBtnSecondary, isWideWeb && styles.wideGridItem, pressed && styles.heroBtnPressed]}
          onPress={() => router.push('/(app)/professionals')}
          accessibilityRole="button"
          accessibilityLabel={t('home.findProfessionals')}
        >
          <Ionicons name="briefcase-outline" size={22} color={GOLD} />
          {newOffersOnMyRequestsCount > 0 && (
            <View style={[styles.tileDot, styles.heroBtnDot, styles.tileDotRed]} />
          )}
          <View style={styles.heroBtnInner}>
            <Text style={styles.heroBtnSecondaryText}>{t('home.findProfessionals')}</Text>
            {nearbyProfessionalsCount > 0 && (
              <View style={[styles.nearbyRequestsBadge, { shadowColor: GOLD, shadowOpacity: 0.5 }]}>
                <Text numberOfLines={2} style={[styles.nearbyRequestsBadgeText, styles.slotTextWrap, { color: GOLD }]}>
                  {t('home.nearbyProfessionalsHint', { count: nearbyProfessionalsCount })}
                </Text>
              </View>
            )}
          </View>
        </Pressable>

        {/* ── Εκκρεμείς Προσφορές ── */}
        {breakdown.offers > 0 && (
          <Pressable
            style={({ pressed }) => [styles.card, isWideWeb && styles.wideGridItem, pressed && styles.cardPressed]}
            onPress={() => router.push('/(app)/offers')}
            accessibilityRole="button"
          >
            <View style={styles.iconCircle}>
              <Ionicons name="send-outline" size={20} color={GOLD} />
            </View>
            <View style={styles.cardText}>
              <Text style={styles.cardTitle}>{t('home.incomingOffers')}</Text>
              <Text style={styles.cardSubtitle}>
                {t('home.offersWaiting', { count: breakdown.offers })}
              </Text>
            </View>
            <View style={styles.cardRight}>
              <Badge count={breakdown.offers} pulse />
              <Ionicons name="chevron-forward" size={20} color={colors.textMuted} />
            </View>
          </Pressable>
        )}

        {/* ── Κουμπιά: Νέα συμφωνία / Οι συμφωνίες μου ── */}
        <View ref={newRef} style={styles.btnRow}>
          <Pressable
            style={({ pressed }) => [styles.heroBtn, styles.heroBtnBig, styles.flex1, pressed && styles.heroBtnPressed]}
            onPress={() => router.push('/(app)/agreements/new')}
            accessibilityRole="button"
          >
            <Ionicons name="add-circle" size={22} color={NAVY} />
            <View style={styles.heroBtnInner}>
              <Text style={styles.heroBtnText}>{t('home.action.newAgreement')}</Text>
              {tickerSlides.length > 0 && (() => {
                const slide = tickerSlides[tickerIndex] ?? tickerSlides[0];
                return (
                  <Animated.Text
                    numberOfLines={2}
                    style={[styles.heroBtnHint, styles.slotTextWrap, { opacity: tickerFade }]}
                  >
                    {slide.text}
                  </Animated.Text>
                );
              })()}
            </View>
          </Pressable>
          <Pressable
            ref={agreementsRef}
            style={({ pressed }) => [styles.heroBtnSecondary, styles.heroBtnBig, styles.flex1, pressed && styles.heroBtnPressed]}
            onPress={() => router.push('/(app)/agreements')}
            accessibilityRole="button"
          >
            {breakdown.agreements > 0 && (
              <View style={[styles.tileDot, styles.heroBtnDot]} />
            )}
            <Ionicons name="document-text-outline" size={22} color={GOLD} />
            <View style={styles.heroBtnInner}>
              <Text style={styles.heroBtnSecondaryText}>{t('home.myAgreements')}</Text>
              {(activeOneOff + activeOngoing) > 0 && (
                <Text numberOfLines={2} style={[styles.heroBtnSecondaryCaption, styles.slotTextWrap]}>
                  {t('home.activeAgreementsCaption', { count: activeOneOff + activeOngoing })}
                </Text>
              )}
            </View>
          </Pressable>
        </View>

        {/* ── Εργαλεία Επαγγελματία — μόνο για ενεργό επαγγελματικό λογαριασμό ── */}
        {profile?.portfolio_enabled && (
          // Η ενότητα έχει δική της εσωτερική διάταξη (τίτλος + γραμμή
          // κουμπιών + προώθηση), οπότε πιάνει ολόκληρη γραμμή στο grid.
          <View style={[styles.proSection, isWideWeb && styles.wideGridFull]}>
          <Text style={styles.sectionHeader}>{t('home.section_proTools')}</Text>
          <View style={styles.btnRow}>
            <Pressable
              style={({ pressed }) => [styles.proBtn, styles.flex1, pressed && styles.heroBtnPressed]}
              onPress={() => router.push('/(app)/offers/sent')}
              accessibilityRole="button"
              accessibilityLabel={t('home.myOffers')}
            >
              <Ionicons name="send-outline" size={20} color={colors.professional} />
              <Text style={styles.proBtnText}>{t('home.myOffers')}</Text>
            </Pressable>
            <Pressable
              style={({ pressed }) => [styles.proBtn, styles.flex1, pressed && styles.heroBtnPressed]}
              onPress={() => router.push('/(app)/find-clients' as any)}
              accessibilityRole="button"
              accessibilityLabel={t('home.findClients')}
            >
              {newRequestsCount > 0 && (
                <View style={[styles.tileDot, styles.heroBtnDot]} />
              )}
              <Ionicons name="people-outline" size={20} color={colors.professional} />
              <View style={{ flex: 1, alignItems: 'center' }}>
                <Text style={styles.proBtnText}>{t('home.findClients')}</Text>
                {nearbyMatchingRequestsCount > 0 && (
                  <Animated.View
                    style={[
                      styles.nearbyRequestsBadge,
                      {
                        transform: [{ scale: nearbyBounceAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] }) }],
                        shadowOpacity: nearbyGlowAnim.interpolate({ inputRange: [0, 1], outputRange: [0.25, 0.9] }),
                      },
                    ]}
                  >
                    <Text
                      numberOfLines={2}
                      style={[styles.nearbyRequestsBadgeText, styles.slotTextWrap]}
                    >
                      {t('home.nearbyRequestsHint', { count: nearbyMatchingRequestsCount })}
                    </Text>
                  </Animated.View>
                )}
              </View>
            </Pressable>
          </View>
          <Pressable
            style={({ pressed }) => [
              styles.sponsorTile,
              !profile?.portfolio_public && styles.sponsorTileDisabled,
              !!mySponsoredListing && styles.sponsorTileActive,
              pressed && styles.heroBtnPressed,
            ]}
            onPress={onPromoteProfilePress}
            accessibilityRole="button"
            accessibilityLabel={t('home.promoteProfile')}
          >
            <View>
              <Ionicons
                name={mySponsoredListing ? 'megaphone' : 'megaphone-outline'}
                size={15}
                color={mySponsoredListing ? colors.textOnPrimary : profile?.portfolio_public ? colors.primary : colors.textMuted}
              />
              {!profile?.portfolio_public && !mySponsoredListing && (
                <View style={styles.sponsorTileLockBadge}>
                  <Ionicons name="lock-closed" size={9} color={colors.textOnPrimary} />
                </View>
              )}
            </View>
            <Text
              style={[
                styles.sponsorTileText,
                !profile?.portfolio_public && styles.sponsorTileTextDisabled,
                !!mySponsoredListing && styles.sponsorTileTextActive,
              ]}
            >
              {mySponsoredListing
                ? t('home.promoteProfileActive', { rate: mySponsoredListing.daily_rate })
                : t('home.promoteProfile')}
            </Text>
          </Pressable>
          </View>
        )}

        {/* ── Ειδήσεις ──
            Κάτω από τα εργαλεία επαγγελματία, ώστε όλες οι ενέργειες να
            μαζεύονται στο πάνω μέρος. Για μη επαγγελματίες το παραπάνω μπλοκ
            δεν αποδίδεται, οπότε η κάρτα ανεβαίνει φυσικά στη θέση του. */}
        <Pressable
          style={({ pressed }) => [styles.card, isWideWeb && styles.wideGridItem, pressed && styles.cardPressed]}
          onPress={() => { setHasNewNews(false); router.push('/(app)/news'); }}
          accessibilityRole="button"
          accessibilityLabel={t('home.news')}
        >
          <View style={styles.iconCircle}>
            <Ionicons name="newspaper-outline" size={20} color={GOLD} />
          </View>
          <View style={styles.cardText}>
            <Text style={styles.cardTitle}>{t('home.news')}</Text>
            <View style={styles.newsSubtitleSlot}>
              {!!newsTitles[newsTitleIndex] && (
                <Animated.Text style={[styles.cardSubtitle, { opacity: newsTitleFade }]} numberOfLines={2}>
                  {newsTitles[newsTitleIndex]}
                </Animated.Text>
              )}
            </View>
          </View>
          <View style={styles.cardRight}>
            {hasNewNews && <View style={styles.newsDot} />}
            <Ionicons name="chevron-forward" size={20} color={colors.textMuted} />
          </View>
        </Pressable>

        </View>

        <View style={styles.flex1} />

        <View style={styles.footerGroup}>
          {/* Ημερήσια παρουσία: η ανταμοιβή είναι μελάνι, γι' αυτό κάθεται
              ακριβώς πάνω από την ένδειξη μελανιού. Το refreshProfile
              ενημερώνει το υπόλοιπο μόλις πιστωθεί. */}
          <DailyCheckin onRewarded={() => void refreshProfile()} />

          {/* Πάντα δείχνει το μελάνι (πληρωμένο) στο κάτω μέρος — οι δωρεάν
              υπογραφές φαίνονται ήδη μέσα στο κουμπί "Νέα Συμφωνία", δεν
              χρειάζεται να επαναλαμβάνονται εδώ. Η μπάρα εμφανίζεται μόνο
              αφού έχει γίνει έστω μία πραγματική αγορά (υπάρχει σημείο
              αναφοράς) — πριν από αυτό δεν υπάρχει "max" να συγκριθεί. */}
          <Pressable onPress={() => router.push('/(app)/wallet/topup')} accessibilityRole="button">
            <Text style={walletBalance >= 1 ? styles.walletInkLink : styles.noInkHint} numberOfLines={1} adjustsFontSizeToFit>
              {walletBalance >= 1
                ? t('home.newAgreementHint_ink', { count: Math.floor(walletBalance) })
                : t('home.noInkHint')}
            </Text>
            {inkFillRatio != null && (
              <View style={styles.inkBarTrack}>
                <View style={[styles.inkBarFill, walletBalance < 1 && styles.inkBarFillEmpty, { width: `${Math.round(inkFillRatio * 100)}%` }]} />
              </View>
            )}
          </Pressable>

          {platformStats.length > 0 && (
            <Animated.Text style={[styles.platformStat, { opacity: platformStatFade }]}>
              {platformStats[platformStatIndex % platformStats.length]}
            </Animated.Text>
          )}

          <DisclaimerNote style={styles.disclaimerNote} compact />
        </View>
      </ScrollView>

      {coachTargets && <Coachmarks targets={coachTargets} measure={measureRef} onDone={finishCoach} />}

      <Modal visible={showFreeModal} transparent animationType="fade" onRequestClose={() => setShowFreeModal(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setShowFreeModal(false)}>
          <Pressable style={[styles.modalSheet, { paddingBottom: insets.bottom + spacing.lg }]} onPress={() => {}}>
            <View style={styles.modalHandle} />
            <View style={styles.modalHeader}>
              <Ionicons name="diamond" size={28} color={GOLD} />
              <Text style={styles.modalTitle}>{t('home.freeModalTitle')}</Text>
            </View>
            <Text style={styles.modalBody}>
              {t('home.freeModalIntroPrefix')} <Text style={styles.modalBold}>{t('home.freeModalIntroBold')}</Text>{t('home.freeModalIntroSuffix')}
            </Text>
            {[
              { icon: 'gift-outline', title: t('home.freeModalWelcomeTitle'), desc: t('home.freeModalWelcomeDesc') },
              { icon: 'trophy-outline', title: t('home.freeModalLevelUpTitle'), desc: t('home.freeModalLevelUpDesc') },
            ].map((item) => (
              <View key={item.title} style={styles.modalRow}>
                <View style={styles.modalIconWrap}>
                  <Ionicons name={item.icon as keyof typeof Ionicons.glyphMap} size={20} color={GOLD} />
                </View>
                <View style={styles.modalRowText}>
                  <Text style={styles.modalRowTitle}>{item.title}</Text>
                  <Text style={styles.modalRowDesc}>{item.desc}</Text>
                </View>
              </View>
            ))}
            <Pressable style={styles.modalClose} onPress={() => setShowFreeModal(false)} accessibilityRole="button">
              <Text style={styles.modalCloseText}>{t('home.freeModalDismiss')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

    </View>

    {/* ── Bell notification panel (Facebook-style dropdown) ── */}
    <Modal
      visible={showBellPanel}
      transparent
      animationType="fade"
      onRequestClose={() => setShowBellPanel(false)}
    >
      <Pressable style={[styles.bellOverlay, { paddingTop: insets.top + 116 }]} onPress={() => setShowBellPanel(false)}>
        <Pressable style={styles.bellPanel} onPress={() => {}}>
          <View style={styles.bellPanelHeader}>
            <Text style={styles.bellPanelTitle}>{t('home.notifications')}</Text>
            <Pressable onPress={() => setShowBellPanel(false)} hitSlop={10} accessibilityRole="button" accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={22} color={colors.textMuted} />
            </Pressable>
          </View>

          <ScrollView
            style={{ maxHeight: screenHeight * 0.65 }}
            showsVerticalScrollIndicator
            indicatorStyle="white"
            nestedScrollEnabled
          >
            {notifLoading ? (
              <ActivityIndicator color={GOLD} style={{ marginTop: spacing.xl, marginBottom: spacing.xl }} />
            ) : notifications.length === 0 ? (
              <View style={styles.bellEmpty}>
                <Text style={styles.bellEmptyIcon}>🔔</Text>
                <Text style={styles.bellEmptyText}>{t('home.noNotifications')}</Text>
                <Text style={styles.bellEmptySubtext}>{t('home.noNotificationsBody')}</Text>
              </View>
            ) : (
              <>
                {notifications.some((n) => n.isEvent ? !n.readAt : true) && (
                  <Pressable
                    style={styles.bellMarkAll}
                    onPress={() => {
                      void markAllNotificationsRead().then(() => {
                        setNotifications((prev) => prev.map((n) => n.isEvent ? { ...n, readAt: new Date().toISOString() } : n));
                        refreshNeedsAction();
                      });
                    }}
                  >
                    <Text style={styles.bellMarkAllText}>{t('home.markAllRead')}</Text>
                  </Pressable>
                )}
                {notifications.map((n) => {
                  // Για notification_events: unread αν read_at IS NULL
                  // Για actionable items: πάντα unread (χρειάζονται ενέργεια)
                  const isUnread = n.isEvent ? !n.readAt : true;
                  return (
                    <Pressable
                      key={n.id}
                      style={({ pressed }) => [
                        styles.bellItem,
                        isUnread && styles.bellItemUnread,
                        pressed && styles.bellItemPressed,
                      ]}
                      onPress={() => {
                        // Σήμανση ως αναγνωσμένο αν είναι event
                        if (n.isEvent && n.eventId && !n.readAt) {
                          void markNotificationRead(n.eventId).then(() => {
                            setNotifications((prev) =>
                              prev.map((item) =>
                                item.eventId === n.eventId
                                  ? { ...item, readAt: new Date().toISOString() }
                                  : item,
                              ),
                            );
                            refreshNeedsAction();
                          });
                        }
                        setShowBellPanel(false);
                        if (n.params) {
                          router.push({ pathname: n.route as never, params: n.params });
                        } else {
                          router.push(n.route as never);
                        }
                      }}
                    >
                      {isUnread && <View style={styles.bellDot} />}
                      {(() => {
                        const visual = notifVisual(n.type, colors);
                        return (
                          <View style={[styles.bellTypeIcon, { backgroundColor: `${visual.color}20` }]}>
                            <Ionicons name={visual.icon} size={16} color={visual.color} />
                          </View>
                        );
                      })()}
                      <View style={styles.bellItemContent}>
                        <Text style={[styles.bellItemTitle, isUnread && styles.bellItemTitleUnread]}>
                          {n.title}
                        </Text>
                        <Text style={styles.bellItemBody} numberOfLines={2}>{n.body}</Text>
                        <Text style={styles.bellItemTime}>{formatDateTime(n.timestamp)}</Text>
                      </View>
                      <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                    </Pressable>
                  );
                })}
                {hasMoreEvents && (
                  <Pressable
                    style={styles.bellLoadMore}
                    disabled={loadingMoreEvents}
                    onPress={async () => {
                      const oldestEvent = [...notifications].reverse().find((n) => n.isEvent);
                      if (!oldestEvent) return;
                      setLoadingMoreEvents(true);
                      try {
                        const page = await fetchNotificationEventsPage(user!.id, {
                          before: oldestEvent.timestamp,
                        });
                        setHasMoreEvents(page.hasMore);
                        setNotifications((prev) => [...prev, ...page.events]);
                      } catch {
                        /* αγνόησε — ο χρήστης μπορεί να ξαναδοκιμάσει */
                      } finally {
                        setLoadingMoreEvents(false);
                      }
                    }}
                  >
                    {loadingMoreEvents
                      ? <ActivityIndicator color={colors.primary} size="small" />
                      : <Text style={styles.bellLoadMoreText}>{t('home.loadOlder')}</Text>
                    }
                  </Pressable>
                )}
              </>
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>

    {/* ── Chat history panel (τελευταίο μήνυμα ανά συμφωνία, ίδιο pattern με το κουδούνι) ── */}
    <Modal
      visible={showChatPanel}
      transparent
      animationType="fade"
      onRequestClose={() => setShowChatPanel(false)}
    >
      <Pressable style={[styles.bellOverlay, { paddingTop: insets.top + 116 }]} onPress={() => setShowChatPanel(false)}>
        <Pressable style={styles.bellPanel} onPress={() => {}}>
          <View style={styles.bellPanelHeader}>
            <Text style={styles.bellPanelTitle}>{t('home.chat')}</Text>
            <Pressable onPress={() => setShowChatPanel(false)} hitSlop={10} accessibilityRole="button" accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={22} color={colors.textMuted} />
            </Pressable>
          </View>

          <ScrollView
            style={{ maxHeight: screenHeight * 0.65 }}
            showsVerticalScrollIndicator
            indicatorStyle="white"
            nestedScrollEnabled
          >
            {chatPreviewsLoading ? (
              <ActivityIndicator color={GOLD} style={{ marginTop: spacing.xl, marginBottom: spacing.xl }} />
            ) : chatPreviews.length === 0 ? (
              <View style={styles.bellEmpty}>
                <Text style={styles.bellEmptyIcon}>💬</Text>
                <Text style={styles.bellEmptyText}>{t('home.noChats')}</Text>
              </View>
            ) : (
              chatPreviews.map((c) => (
                <Pressable
                  key={c.agreement_id}
                  style={({ pressed }) => [
                    styles.bellItem,
                    c.unread && styles.bellItemUnread,
                    pressed && styles.bellItemPressed,
                  ]}
                  onPress={() => {
                    if (c.unread) {
                      void markAgreementMessagesRead(c.agreement_id).then(() => refreshNeedsAction());
                    }
                    setShowChatPanel(false);
                    router.push({ pathname: '/(app)/agreements/messages/[id]' as any, params: { id: c.agreement_id } });
                  }}
                >
                  {c.unread && <View style={styles.bellDot} />}
                  <View style={[styles.bellTypeIcon, { backgroundColor: `${GOLD}20` }]}>
                    <Ionicons name="chatbubble-ellipses-outline" size={16} color={GOLD} />
                  </View>
                  <View style={styles.bellItemContent}>
                    <Text style={[styles.bellItemTitle, c.unread && styles.bellItemTitleUnread]} numberOfLines={1}>
                      {c.agreement_title}
                    </Text>
                    <Text style={styles.bellItemBody} numberOfLines={2}>
                      {c.last_message_body}
                    </Text>
                    <Text style={styles.bellItemTime}>{formatDateTime(c.last_message_created_at)}</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                </Pressable>
              ))
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>

    <ConfirmModal
      visible={showPrivateSponsorPrompt}
      title={t('home.promoteProfile')}
      message={t('home.promotePrivateBody')}
      confirmLabel={t('home.promoteMakePublic')}
      cancelLabel={t('common.cancel')}
      loading={makingPublicBusy}
      onConfirm={onMakePublicAndPromote}
      onCancel={() => setShowPrivateSponsorPrompt(false)}
    />

    </TabScreenWrapper>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  container: {
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  containerGrow: {
    flexGrow: 1,
  },

  // ── Desktop web ──
  // Σε πλατιά οθόνη οι ενέργειες μπαίνουν σε δύο στήλες αντί να τεντώνονται
  // σε όλο το πλάτος. Το minWidth κρατάει τα πλακίδια αναγνώσιμα· αν δεν
  // χωρέσουν δύο, το flexWrap τα ξαναβάζει σε μία στήλη μόνο του.
  wideGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  wideGridItem: {
    flexBasis: '48%',
    flexGrow: 1,
    minWidth: 280,
  },
  wideGridFull: {
    flexBasis: '100%',
  },
  proSection: {
    gap: spacing.md,
  },
  // Σε κινητό ο wrapper του grid πρέπει να διατηρήσει το κενό που έδινε ο
  // contentContainer όταν τα στοιχεία ήταν απευθείας παιδιά του.
  stackGap: {
    gap: spacing.md,
  },

  // Header
  headerGroup: {
    gap: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  avatarCol: {
    alignItems: 'center',
    gap: 4,
  },
  avatarWrap: {
    position: 'relative',
  },
  levelUnderAvatar: {
    alignSelf: 'center',
  },
  verifiedOverlay: {
    position: 'absolute',
    bottom: -2,
    right: -4,
  },
  headerMid: {
    flex: 1,
    gap: 2,
  },
  greeting: {
    color: colors.textMuted,
    fontSize: fontSize.md,
  },
  greetingName: {
    color: colors.text,
    fontWeight: '700',
  },
  userIdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  userIdText: {
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerBtn: {
    alignItems: 'center',
    gap: 3,
    position: 'relative',
  },
  circleLabel: {
    color: GOLD,
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  circleCol: {
    alignItems: 'center',
    gap: 3,
  },
  bellBtn: {
    padding: 6,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatGlowBtn: {
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.danger,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  bellBadgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
  },
  circleIconOuter: {
    position: 'relative',
  },
  circleIconWrap: {
    width: 54,
    height: 54,
    borderRadius: 27,
    borderWidth: 1.5,
    borderColor: colors.borderStrong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  circleIconWrapFilled: {
    borderColor: colors.primary,
    paddingHorizontal: 6,
  },
  circleIconInner: {
    alignItems: 'center',
    gap: 0,
  },
  circleIconCount: {
    color: colors.primary,
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 22,
  },
  circleIconSub: {
    color: colors.primary,
    fontSize: 8,
    fontWeight: '600',
    textAlign: 'center',
    letterSpacing: 0.2,
  },
  circleBadgeRed: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
    borderWidth: 1.5,
    borderColor: colors.surface,
  },
  circleBadgeMuted: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(201,168,67,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
    borderWidth: 1.5,
    borderColor: colors.surface,
  },
  circleBadgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '800',
    lineHeight: 12,
  },
  announcementDot: {
    position: 'absolute',
    top: '50%',
    marginTop: -7,
    right: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#3B82F6',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.surface,
  },

  // Στατιστικά (βαθμολογία / αξιοπιστία) — δίπλα-δίπλα, σταθερά, άχρωμα
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 6,
    marginTop: 3,
  },
  statChip: {
    fontSize: 11,
    color: colors.textMuted,
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.full,
    paddingHorizontal: 8,
    paddingVertical: 2,
    overflow: 'hidden',
  },

  // Level progress bar
  progressWrap: {
    gap: 5,
  },
  progressText: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '500',
  },
  progressTrack: {
    height: 5,
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: 5,
    borderRadius: 3,
  },

  betaBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  betaBannerBody: {
    flex: 1,
    gap: 2,
  },
  betaBannerText: {
    color: colors.textMuted,
    fontSize: fontSize.xs,
    lineHeight: 15,
  },
  betaBannerLink: {
    color: colors.textMuted,
    fontSize: fontSize.xs,
    fontWeight: '600',
    lineHeight: 15,
    textDecorationLine: 'underline',
  },
  // Agreements card
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
  },
  cardPressed: { opacity: 0.75 },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(201,168,67,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardText: {
    flex: 1,
    gap: 2,
  },
  cardTitle: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  cardSubtitle: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    lineHeight: 17,
  },
  newsSubtitleSlot: {
    height: 38,
    justifyContent: 'center',
  },
  cardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  activePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: colors.success,
    borderRadius: radius.full,
    paddingVertical: 3,
    paddingHorizontal: spacing.sm,
  },
  activeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.background,
  },
  activeText: {
    color: colors.background,
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  ongoingNote: {
    color: colors.textMuted,
    fontSize: 11,
    marginTop: 1,
  },

  // Buttons row
  btnRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  flex1: { flex: 1 },
  offerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: 'rgba(201,168,67,0.12)',
    borderRadius: radius.xl,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(201,168,67,0.35)',
  },
  offerBtnText: {
    color: GOLD,
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  // Στοιχεία που φαίνονται μόνο επειδή έχεις ενεργό επαγγελματικό λογαριασμό —
  // aqua/petrol αντί για το γενικό χρυσό, ώστε να ξεχωρίζουν οπτικά.
  proBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: `${colors.professional}1F`,
    borderRadius: radius.xl,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: `${colors.professional}59`,
    height: 84,
  },
  proBtnText: {
    color: colors.professional,
    fontSize: fontSize.sm,
    fontWeight: '700',
    textAlign: 'center',
    width: '100%',
    lineHeight: 17,
  },
  sponsorTile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: radius.md,
    paddingVertical: 8,
    marginTop: spacing.xs,
  },
  sponsorTileText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700' },
  sponsorTileDisabled: { borderColor: colors.border, opacity: 0.6 },
  sponsorTileTextDisabled: { color: colors.textMuted },
  sponsorTileActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  sponsorTileTextActive: { color: colors.textOnPrimary },
  sponsorTileLockBadge: {
    position: 'absolute',
    bottom: -4,
    right: -6,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.danger,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nearbyRequestsBadge: {
    width: '100%',
    marginTop: 2,
    shadowColor: colors.professional,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 6,
    elevation: 4,
  },
  nearbyRequestsBadgeText: {
    color: colors.professional,
    fontSize: 10,
    fontWeight: '800',
    lineHeight: 13,
  },
  slotTextWrap: {
    textAlign: 'center',
    width: '100%',
  },

  // Hero button
  heroBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: GOLD,
    borderRadius: radius.xl,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  heroBtnPressed: { opacity: 0.85 },
  heroBtnBig: {
    paddingVertical: spacing.md,
    height: 92,
  },
  heroBtnHint: {
    color: 'rgba(13,27,42,0.75)',
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 14,
    marginTop: 1,
  },
  juryDutyTile: {
    backgroundColor: 'rgba(220,38,38,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(220,38,38,0.4)',
    borderRadius: radius.xl,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
    gap: 2,
  },
  juryDutyText: { color: '#DC2626', fontSize: fontSize.sm, fontWeight: '700' },
  juryDutyCountdown: { color: '#DC2626', fontSize: fontSize.xs },
  heroBtnSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: 'rgba(201,168,67,0.12)',
    borderRadius: radius.xl,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(201,168,67,0.35)',
    position: 'relative',
  },
  heroBtnSecondaryText: {
    color: GOLD,
    fontSize: fontSize.sm,
    fontWeight: '800',
    letterSpacing: 0.3,
    textAlign: 'center',
    width: '100%',
    lineHeight: 17,
  },
  heroBtnSecondaryCaption: {
    color: colors.textMuted,
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 13,
    marginTop: 1,
  },
  sectionHeader: {
    color: colors.textMuted,
    fontSize: fontSize.xs,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: -spacing.xs,
  },
  heroBtnDot: {
    position: 'absolute',
    top: 8,
    right: 10,
  },
  heroBtnInner: {
    alignItems: 'center',
    flex: 1,
    gap: 3,
  },
  heroBtnText: {
    color: '#0D1B2A',
    fontSize: fontSize.sm,
    fontWeight: '800',
    letterSpacing: 0.3,
    textAlign: 'center',
    width: '100%',
    lineHeight: 17,
  },
  footerGroup: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  platformStat: {
    textAlign: 'center',
    color: colors.textMuted,
    fontSize: 11,
    opacity: 0.7,
  },
  walletInkLink: {
    textAlign: 'center',
    color: GOLD,
    fontSize: 12,
    fontWeight: '700',
  },
  noInkHint: {
    textAlign: 'center',
    color: colors.danger,
    fontSize: 12,
    fontWeight: '700',
  },
  inkBarTrack: {
    marginTop: 6,
    alignSelf: 'center',
    width: 120,
    height: 5,
    borderRadius: 3,
    backgroundColor: colors.surfaceAlt,
    overflow: 'hidden',
  },
  inkBarFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: GOLD,
  },
  inkBarFillEmpty: {
    backgroundColor: colors.danger,
  },
  disclaimerNote: {
    opacity: 0.6,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  modalSheet: {
    backgroundColor: '#0F2236',
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    padding: spacing.lg,
    gap: spacing.md,
    borderTopWidth: 1,
    borderColor: colors.border,
  },
  modalHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  modalTitle: {
    color: colors.text,
    fontSize: fontSize.xl,
    fontWeight: '800',
  },
  modalBody: {
    color: colors.textMuted,
    fontSize: fontSize.md,
    lineHeight: 22,
  },
  modalBold: {
    color: GOLD,
    fontWeight: '700',
  },
  modalRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  modalIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(201,168,67,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(201,168,67,0.25)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  modalRowText: { flex: 1, gap: 2 },
  modalRowTitle: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  modalRowDesc: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    lineHeight: 20,
  },
  modalClose: {
    marginTop: spacing.sm,
    backgroundColor: GOLD,
    borderRadius: radius.full,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalCloseText: {
    color: '#0D1B2A',
    fontSize: fontSize.md,
    fontWeight: '800',
  },

  // Bell notification panel — Facebook-style dropdown
  bellOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.25)',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
  },
  bellPanel: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    paddingTop: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    width: '100%',
    maxWidth: 480,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.35,
    shadowRadius: 20,
    elevation: 16,
  },
  bellArrow: {
    display: 'none',
  } as never,
  bellPanelHandle: { display: 'none' } as never,
  bellPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    paddingBottom: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  bellPanelTitle: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '800',
  },
  bellMarkAll: {
    alignSelf: 'flex-end',
    paddingVertical: spacing.xs,
    paddingBottom: spacing.sm,
  },
  bellMarkAllText: {
    color: GOLD,
    fontSize: fontSize.xs,
    fontWeight: '600',
  },
  bellLoadMore: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  bellLoadMoreText: {
    color: GOLD,
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  bellEmpty: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    gap: spacing.sm,
  },
  bellEmptyIcon: {
    fontSize: 40,
  },
  bellEmptyText: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  bellEmptySubtext: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    textAlign: 'center',
    lineHeight: 20,
  },
  bellItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    position: 'relative',
  },
  bellItemUnread: {
    backgroundColor: 'rgba(201,168,67,0.06)',
    marginHorizontal: -spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  bellItemPressed: { opacity: 0.7 },
  bellDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: GOLD,
    flexShrink: 0,
  },
  bellTypeIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bellItemContent: {
    flex: 1,
    gap: 2,
  },
  bellItemTitle: {
    color: colors.text,
    fontSize: fontSize.sm,
    fontWeight: '600',
  },
  bellItemTitleUnread: {
    fontWeight: '800',
    color: colors.text,
  },
  bellItemBody: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    lineHeight: 18,
  },
  bellItemTime: {
    color: colors.textMuted,
    fontSize: 11,
    marginTop: 2,
  },
  newsCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  newsCardText: {
    flex: 1,
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontWeight: '600',
  },
  newsDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: colors.primary,
    marginRight: 2,
  },
  tileRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  tile: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    position: 'relative',
  },
  tileIconWrap: {
    position: 'relative',
  },
  tileIconBadge: {
    position: 'absolute',
    bottom: -4,
    right: -6,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: GOLD,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.surfaceAlt,
  },
  tileText: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontWeight: '600',
    textAlign: 'center',
  },
  tileDot: {
    position: 'absolute',
    top: 8,
    right: 10,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: colors.primary,
  },
  tileDotRed: {
    position: 'absolute',
    top: 8,
    right: 10,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#EF4444',
  },
  });
}
